/**
 * Daily Brief — personal "good morning" card shown at the top of the
 * home screen once per day.
 *
 * Pulls data from:
 *   - LocalState (user plate/year/phone)
 *   - License status service (vehicle/license.service)
 *   - The fuel card's cached "nearest cheap station" (if available)
 *   - A static tips list rotating by `dayOfYear % tipsCount`
 *
 * Shows ONCE per local day (per device). LocalStorage flag survives
 * across sessions. User can dismiss to hide for 4 hours.
 */

import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { escape as esc } from '../../shared/escape.js';
import { getStatus as getLicenseStatus } from '../vehicle/license.service.js';
import { createLogger } from '../../core/logger.js';
import { iconHtml as aiIconHtml } from '../ai/icon.js';

const log = createLogger('daily-brief');

const SHOWN_AT_KEY = 'c2c_brief_lastShownAt';
const DISMISSED_AT_KEY = 'c2c_brief_dismissedAt';
const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_TTL_MS = 4 * 60 * 60 * 1000;  // 4 hours

/** Curated list of car-care tips. Rotates daily by `dayOfYear % length`. */
const TIPS = [
  '🛞 לחץ אוויר בקיץ — 2.4 בר במקום 2.2. חוסך דלק ומאריך חיי צמיגים.',
  '⛽ תדלוק בבוקר מוקדם — הדלק קר יותר, צפוף יותר, מקבלים יותר ליטרים לאותו תשלום.',
  '🌡️ לפני נסיעה ארוכה בקיץ — בדוק רמת מים, שמן, ולחץ צמיגים. הגיהינום בכביש בלי AC.',
  '🚗 שמירה על מרחק 2 שניות מהרכב לפניך = מספיק זמן לעצור גם בבלימה פתאומית.',
  '💧 מים מזוקקים למצבר — לא מי ברז! הם פוגעים בחיי המצבר.',
  '🔋 רכב שלא מותנע 10 ימים — המצבר מתחיל להידרדר. הפעל את המנוע פעם בשבוע ל-10 דקות.',
  '🚙 חימום מנוע — לא יותר מ-30 שניות במקום. נסיעה איטית 5 דקות הראשונות חכמה יותר.',
  '⚡ שטח כתב לבד — אם הוא קטן, החלף. צמיג רטוב על שטח קטן = החלקה.',
  '🛣️ נסיעה ב-90 קמ"ש חוסכת ~25% דלק לעומת 110. הפרש של 10 דקות לתל אביב.',
  '🚦 רכב לא בשימוש 30 ימים+ — סכנת קיבעון בלמים. הפעל פעם בשבועיים.',
  '🔧 שמן מנוע — החלף לפי ק"מ או 12 חודשים, מה שמגיע קודם. גם בלי נסיעה הוא מתיישן.',
  '☀️ זכוכית קדמית סדוקה — תיקון מתחת ל-2 ס"מ עולה ~200 ₪. חכה והיא תתפצל ל-500-1500.',
  '🚗 מצנן (אנטיפריז) — אל תוסיף מים פשוטים בקיץ, זה גורם לאלקטרוליזה ופוגע במנוע.',
  '🛞 סיבוב צמיגים כל 10K ק"מ — מאריך את חייהם ב-30%. חבל לקצר.',
  '⛽ בנזין 95 או 98? אם הרכב לא דורש 98 — חיסכון של 30+ ₪ למילוי, ללא הבדל בביצועים.',
  '🌧️ גשם ראשון = החלק ביותר. השמן והאבק על הכביש מתערבבים. סע איטי.',
  '🔥 רכב חונה בשמש — אל תפתח את ה-AC על מקסימום. פתח חלונות 30 שניות קודם.',
  '🚙 מתנע — אל תפעיל יותר מ-10 שניות ברצף. תן 30 שניות מנוחה בין ניסיונות.',
  '💡 פנס מקולקל — לעולם בלילה. תקן עוד היום.',
  '🛣️ נסיעה ב-2 גלגלים על שוליים — שוחק את ההיגוי. דבק במסלול.',
  '🚗 שטיפת רכב מתחת לפנס שמש ישיר = כתמים. שטוף בצל.',
  '🔋 רכב היברידי — נסיעה קצרה מ-5 ק"מ פוגעת בסוללה. שלב נסיעות.',
  '⚙️ הגה רטט — בעיית איזון צמיגים. תיקון 50-100 ₪.',
  '🛢️ ריח שריפה מהמנוע — עצור מיד. סכנת אש או נזק חמור.',
  '🚦 בכביש רטוב — מרחק עצירה מוכפל. כפל את המרחק לפני בלימה.',
  '⛽ דלק כמעט אזל לעולם — הצינור משאב לכלוך מתחתית המכל. תדלק כשנשארו 25%.',
  '🔧 צליל "טיק טיק" מהמנוע — לחץ שמן נמוך. עצור ובדוק מיד.',
  '🚗 בלמים מוקלטים — מצנן בלמים חם או רמת נוזל בלמים נמוכה. בדוק היום.',
  '🛞 הקשבה לרכב — צלילים חדשים = בעיות מתעוררות. אל תתעלם.',
  '💰 ביטוח מקיף — השווה כל שנה. הפרשים של 600-1500 ₪ בין חברות לאותה כיסוי.',
];

const HOST_ID = 'dailyBriefHost';

/** Session-level state — survives across multiple `home.render()` calls in
 *  the same page life. `home.render()` is called many times in this app
 *  (auth events, license events, navigation, etc.), so without this guard
 *  the first render would mark "shown today" via `markShown()`, and every
 *  subsequent render would get `null` from `build()` — making the brief
 *  flicker in and disappear.
 *
 *  Now: per session, build the element once. Re-renders reuse it. Only
 *  dismiss / next-day actually hides it. */
let sessionDecision = /** @type {'unknown' | 'show' | 'hide'} */ ('unknown');
let sessionDismissed = false;

/**
 * The actual DOM element, cached after the first successful build.
 *
 * `home.render()` is invoked several times during a normal page lifecycle
 * (auth:ready → license:status → vehicle:changed → user action → …). Each
 * call runs `host.replaceChildren()` which detaches the brief from the
 * DOM, and `build()` is async (it awaits license data). The empty gap
 * between detach and re-append causes the flash the user sees.
 *
 * By caching the element here we can synchronously re-attach the same
 * node on every re-render — `appendChild` MOVES nodes between parents,
 * so a single instance is enough.
 *
 * Cleared only when the user explicitly dismisses (the ✕ button).
 *
 * @type {HTMLElement | null}
 */
let cachedElement = null;

/**
 * Should we show the brief now?
 * - Not if dismissed THIS session (closed by ✕)
 * - Not if dismissed in last 4h (cross-session, via localStorage)
 *
 * NOTE: We intentionally DON'T enforce "once per day" — the greeting +
 * tip change throughout the day anyway, and the user can always dismiss
 * with ✕. Hard-blocking on a "shown today" flag caused the brief to
 * never reappear in the same session if `home.render()` was called
 * twice (which it is, frequently — auth events, license events, etc.).
 */
function shouldShow() {
  if (sessionDismissed) return false;
  try {
    const dismissed = Number(localStorage.getItem(DISMISSED_AT_KEY) || 0);
    if (Number.isFinite(dismissed) && Date.now() - dismissed < DISMISS_TTL_MS) return false;
    return true;
  } catch (_) { return true; }
}

function markShown() {
  try { localStorage.setItem(SHOWN_AT_KEY, String(Date.now())); }
  catch (_) { /* ignore */ }
}

function markDismissed() {
  try { localStorage.setItem(DISMISSED_AT_KEY, String(Date.now())); }
  catch (_) { /* ignore */ }
}

function greetingForNow() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return '☀️ בוקר טוב';
  if (h >= 12 && h < 17) return '🌤️ צהריים טובים';
  if (h >= 17 && h < 21) return '🌆 ערב טוב';
  return '🌙 לילה טוב';
}

function todayHebrew() {
  const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  const months = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  const d = new Date();
  return `יום ${days[d.getDay()]} · ${d.getDate()} ${months[d.getMonth()]}`;
}

function pickTipForToday() {
  const start = new Date(new Date().getFullYear(), 0, 0);
  const diff = Date.now() - start.getTime();
  const dayOfYear = Math.floor(diff / DAY_MS);
  return TIPS[dayOfYear % TIPS.length];
}

/**
 * Pick the most-relevant AutoMe AI prompt to surface in the brief, based
 * on the user's current state. Falls back to a generic invitation.
 *
 * @param {any} license  License status object (may be empty)
 * @returns {{ label: string, prompt: string }}
 */
function pickAiPrompt(license) {
  if (license && typeof license.daysLeft === 'number') {
    const d = license.daysLeft;
    if (d < 0) {
      return {
        label: 'איך לחדש רישיון שפג? שאל את AutoMe AI',
        prompt: 'הרישיון של הרכב שלי פג. מה התהליך לחידוש ואיזה מסמכים אני צריך?',
      };
    }
    if (d <= 30) {
      return {
        label: 'מתי וכיצד לחדש רישיון? שאל את AutoMe AI',
        prompt: 'הרישיון שלי פג בעוד ' + d + ' ימים. מה התהליך ומה כדאי לעשות?',
      };
    }
  }
  // Daily-rotated generic prompt — keeps the chip feeling fresh.
  const GENERIC = [
    { label: 'כמה הרכב שלי שווה היום? שאל את AutoMe AI',
      prompt: 'כמה הרכב שלי שווה בשוק היד 2 כיום? תן הערכה.' },
    { label: 'איפה הכי כדאי לעשות טסט? שאל את AutoMe AI',
      prompt: 'איפה הכי כדאי לעשות טסט שנתי לרכב שלי? מה לבדוק לפני?' },
    { label: 'מה לבדוק לפני נסיעה ארוכה? שאל את AutoMe AI',
      prompt: 'מה אני צריך לבדוק ברכב לפני נסיעה ארוכה?' },
    { label: 'איך לחסוך בדלק? שאל את AutoMe AI',
      prompt: 'איך אני יכול לחסוך בצריכת דלק עם הרכב שלי?' },
    { label: 'מתי לחדש ביטוח? שאל את AutoMe AI',
      prompt: 'מתי כדאי להתחיל להשוות ביטוחים לפני חידוש?' },
    { label: 'שאל את AutoMe AI כל שאלה על הרכב שלך',
      prompt: '' },
  ];
  const start = new Date(new Date().getFullYear(), 0, 0);
  const dayOfYear = Math.floor((Date.now() - start.getTime()) / DAY_MS);
  return GENERIC[dayOfYear % GENERIC.length];
}

/**
 * Synchronously return the cached daily-brief element, or null if it
 * hasn't been built yet (first render) or was dismissed.
 *
 * Callers (`homeView.render`) use this to re-attach the brief
 * immediately during a re-render, before the async `build()` resolves —
 * eliminating the half-second flash that used to appear when the brief
 * was rebuilt from scratch.
 *
 * @returns {HTMLElement | null}
 */
export function getCachedElement() {
  if (sessionDismissed) return null;
  if (sessionDecision === 'hide') return null;
  return cachedElement;
}

/**
 * Render the daily brief into a host element inserted BEFORE #homeFeed.
 * Called from homeView.render(). Returns the element so it can be inserted.
 *
 * Idempotent across renders WITHIN a session: subsequent calls reuse the
 * cached element built by the first one (no flicker, no double-mark).
 * Across sessions / days it respects the localStorage "shown today" flag.
 *
 * @returns {Promise<HTMLElement|null>}  null if the brief shouldn't show
 */
export async function build() {
  if (sessionDismissed) return null;
  if (sessionDecision === 'hide') return null;

  if (sessionDecision === 'unknown') {
    sessionDecision = shouldShow() ? 'show' : 'hide';
  }
  if (sessionDecision === 'hide') return null;

  // If the element has already been built this session, hand back the
  // same instance. `appendChild()` moves it to the new parent — no flash.
  if (cachedElement) return cachedElement;

  const plate = normalizePlate(LocalState.plate ?? '');
  /** @type {any} */ let license = {};
  if (plate) {
    try { license = await getLicenseStatus(plate); } catch (_) { /* ignore */ }
  }

  // Build rows.
  const rows = [];

  // Row 1 — fuel (from fuel card's GPS cache if available)
  rows.push(`
    <div class="brief-row">
      <span class="brief-icon">⛽</span>
      <span class="brief-text">דלק זול היום: בדוק בכרטיס "דלק זול" למטה</span>
    </div>
  `);

  // Row 2 — license reminder if soon-to-expire
  if (license && typeof license.daysLeft === 'number') {
    const d = license.daysLeft;
    if (d <= 30 && d >= 0) {
      rows.push(`
        <div class="brief-row brief-row-warn">
          <span class="brief-icon">⚠️</span>
          <span class="brief-text">רישיון רכב פג בעוד ${d} ימים — שווה לחדש עכשיו</span>
        </div>
      `);
    } else if (d < 0) {
      rows.push(`
        <div class="brief-row brief-row-bad">
          <span class="brief-icon">🚨</span>
          <span class="brief-text">רישיון רכב פג לפני ${Math.abs(d)} ימים. חידוש דחוף!</span>
        </div>
      `);
    }
  }

  // Row 3 — daily tip
  const tip = pickTipForToday();
  rows.push(`
    <div class="brief-row brief-row-tip">
      <span class="brief-icon">💡</span>
      <span class="brief-text"><strong>הטיפ של היום:</strong> ${esc(tip)}</span>
    </div>
  `);

  // Row 4 — contextual AutoMe AI chip. The prompt adapts to what the user
  // is most likely to care about right now:
  //   - License expired / expiring soon  →  "מה צריך לעשות לחידוש רישיון?"
  //   - Otherwise                         →  generic open-ended invitation
  const aiPrompt = pickAiPrompt(license);
  rows.push(`
    <button type="button" class="brief-ai-chip" data-ai-prompt="${esc(aiPrompt.prompt)}">
      <span class="brief-ai-icon">${aiIconHtml(28)}</span>
      <span class="brief-ai-text">${esc(aiPrompt.label)}</span>
      <span class="brief-ai-chev">›</span>
    </button>
  `);

  const el = document.createElement('div');
  el.id = HOST_ID;
  el.className = 'daily-brief';
  el.innerHTML = `
    <div class="brief-header">
      <div>
        <div class="brief-greeting">${greetingForNow()}</div>
        <div class="brief-date">${todayHebrew()}</div>
      </div>
      <button type="button" class="brief-close" aria-label="סגור">✕</button>
    </div>
    <div class="brief-body">${rows.join('')}</div>
  `;

  el.querySelector('.brief-close')?.addEventListener('click', () => {
    sessionDismissed = true;
    cachedElement = null;  // forget the cached node — it's about to be removed
    markDismissed();
    el.style.opacity = '0';
    el.style.transform = 'scale(0.96)';
    setTimeout(() => el.remove(), 200);
  });

  // Open AutoMe AI with the contextual prompt pre-filled. We rely on
  // `window.openAiChat(preset)` which bootstrap.js wires to view.open().
  el.querySelector('.brief-ai-chip')?.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */ (e.currentTarget);
    const preset = target.getAttribute('data-ai-prompt') || '';
    const w = /** @type {any} */ (window);
    if (typeof w.openAiChat === 'function') w.openAiChat(preset);
  });

  markShown();
  cachedElement = el;  // cache for subsequent renders — no rebuild, no flash
  log.info('daily brief shown');
  return el;
}
